import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Blockquote } from '#src/atoms/layout/Blockquote/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineXS } from '#src/atoms/typography/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

import type {
  UnblockEmailMutation,
  UnblockEmailMutationVariables,
} from './__generated__/index.graphql.ts';

const UNBLOCK_EMAIL_MUTATION = gql`
  mutation UnblockEmailMutation($input: UnblockEmailInput!) {
    unblockEmail(input: $input) {
      currentUser {
        slug
        userSettings {
          id
          disableAllEmails
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<UnblockEmailMutation, UnblockEmailMutationVariables>;

const Content = styled.div`
  display: flex;
  gap: var(--unit);
  justify-content: space-between;
  flex-direction: column;

  @media ${tabletAndAbove} {
    flex-direction: row;
    align-items: center;
  }
`;

export const DisabledEmailWarning = () => {
  const [pending, setPending] = useState(false);
  const { currentUser } = useCurrentUserContext();

  const [unblockEmail] = useMutation(UNBLOCK_EMAIL_MUTATION);

  const requestUnblockEmail = () => {
    setPending(true);

    unblockEmail({
      variables: { input: {} },
    });
  };

  if (!currentUser) {
    return null;
  }

  const {
    userSettings: { disableAllEmails },
  } = currentUser;

  if (!disableAllEmails) {
    return null;
  }
  return (
    <Blockquote variant="red">
      <Content>
        <Vertical gap={2}>
          <Vertical>
            <HeadlineXS as="h6">
              <FormattedMessage defaultMessage="You cannot receive Sorare emails" />
            </HeadlineXS>
            <BodyM>
              <FormattedMessage defaultMessage="You may have marked our emails as spam or your address is invalid." />
            </BodyM>
          </Vertical>
          {pending ? (
            <div>
              <small>
                <FormattedMessage defaultMessage="You should soon be able to receive Sorare emails." />
              </small>
            </div>
          ) : (
            <Button
              size="small"
              color="primary"
              onClick={requestUnblockEmail}
              disabled={pending}
            >
              <FormattedMessage defaultMessage="Unblock Sorare emails" />
            </Button>
          )}
        </Vertical>
      </Content>
    </Blockquote>
  );
};
